<template>
  <div>
    <!-- Hamburger / X Button -->
    <button class="hamburger" @click="toggleMenu">
      <span :class="{ open: isMenuOpen }"></span>
      <span :class="{ open: isMenuOpen }"></span>
      <span :class="{ open: isMenuOpen }"></span>
    </button>

    <!-- Aside Menu -->
    <div :class="['aside-menu', { 'menu-open': isMenuOpen }]">
      <!-- Home Link -->
      <router-link to="/" :class="{ 'active-link': isActiveRoute('/') }" @click="closeMenu">
        <span class="menu-item">Dashboard</span>
      </router-link>

      <!-- Categories dinamiche ordinate alfabeticamente TODO da mappare a be-->
      <template v-if="sortedCategories.length > 0">
        <router-link
          v-for="category in sortedCategories"
          :key="category"
          :to="`/category/${category}`"
          :class="{ 'active-link': isActiveRoute(`/category/${category}`) }"
          @click="closeMenu"
        >
          <span class="menu-item">{{ getCategoryDisplayName(category) }}</span>
        </router-link>
      </template>

      <!-- Loading state -->
      <div v-else-if="isLoading" class="menu-loading">
        <span class="menu-item">Caricamento...</span>
      </div>

      <!-- Error state -->
      <div v-else-if="hasError" class="menu-error">
        <span class="menu-item">Errore caricamento</span>
        <button @click="loadCategories" class="retry-btn">Riprova</button>
      </div>

      <!-- Health Check - solo per admin -->
      <router-link
        v-if="isAdmin"
        to="/health"
        :class="{ 'active-link': isActiveRoute('/health') }"
        @click="closeMenu"
      >
        <span class="menu-item">Health Check</span>
      </router-link>

      <!-- Logout button - sempre in basso -->
      <div class="logout-container">
        <button class="logout-btn" @click="handleLogout">
          <span class="menu-item">Logout</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getCategories } from '@/services/scrapingService'
import authService from '@/services/authService'

// Router
const router = useRouter()

// Composables
const route = useRoute()

// State
const isMenuOpen = ref(false)
const categories = ref<string[]>([])
const isLoading = ref(true)
const hasError = ref(false)

// Mapping dei nomi delle categorie (soluzione temporanea) , devo mappare a BE
const categoryNamesMap: Record<string, string> = {
  pa: 'Pubbliche Amministrazioni',
  pubbliche_amministrazioni: 'Pubbliche Amm.',
  cciaa: 'CCIAA',
  camera_di_commercio: 'CCIAA',
}

// Computed property per ordinamento alfabetico
const sortedCategories = computed(() => {
  return [...categories.value].sort((a, b) => {
    const nameA = getCategoryDisplayName(a).toLowerCase()
    const nameB = getCategoryDisplayName(b).toLowerCase()
    return nameA.localeCompare(nameB, 'it', {
      sensitivity: 'base',
      numeric: true,
      caseFirst: 'lower',
    })
  })
})

// Computed property per verificare se l'utente è admin
const isAdmin = computed(() => {
  const user = authService.getUser()
  return user?.username?.toLowerCase() === 'admin'
})

// Methods
const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
}

const closeMenu = () => {
  isMenuOpen.value = false
}

const isActiveRoute = (path: string): boolean => {
  if (path === '/') {
    return route.path === '/'
  }
  return route.path === path || route.path.startsWith(`${path}/`)
}

const getCategoryDisplayName = (category: string): string => {
  // Prima controlla se c'è un mapping specifico
  const mappedName = categoryNamesMap[category.toLowerCase()]
  if (mappedName) {
    return mappedName
  }

  // Altrimenti usa la formattazione standard
  return category.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
}

const loadCategories = async () => {
  try {
    isLoading.value = true
    hasError.value = false

    console.log('Caricamento categorie...')
    const response = await getCategories()
    categories.value = response.data.categories || []

    console.log('Categorie caricate:', categories.value)
    console.log(
      'Nomi visualizzati (ordinati):',
      sortedCategories.value.map((cat) => ({
        key: cat,
        display: getCategoryDisplayName(cat),
      })),
    )
  } catch (error) {
    console.error('Errore nel caricamento delle categorie:', error)
    hasError.value = true
  } finally {
    isLoading.value = false
  }
}

const handleLogout = () => {
  authService.logout()
  closeMenu()
  router.push('/login')
}

// Lifecycle
onMounted(() => {
  loadCategories()
})
</script>

<style scoped>
/* ======== COMMON ======== */
.aside-menu {
  display: flex;
  flex-direction: column;
  background-color: #3f403e;
  height: 100vh;
  width: 150px;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  overflow-y: auto;
  padding-bottom: 60px; /* Spazio per il logout button */
}

.aside-menu a {
  padding: 8px;
  text-decoration: none;
  font-size: 18px;
  color: white;
  transition: 0.3s;
  display: block;
  border-bottom: 1px solid white;
}

.active-link,
.aside-menu a:hover {
  background-color: #8e8f8d;
  border-left: 1px solid red;
}

.menu-item {
  margin-left: 20px;
  font-size: 14px;
  color: white;
  font-family: Arial, Helvetica, sans-serif;
}

.menu-loading,
.menu-error {
  padding: 8px;
  border-bottom: 1px solid white;
}

.menu-loading .menu-item {
  color: #ccc;
  font-style: italic;
}

.menu-error .menu-item {
  color: #ff6b6b;
  font-style: italic;
}

.retry-btn {
  background: none;
  border: none;
  color: #ff6b6b;
  cursor: pointer;
  font-size: 12px;
  margin-left: 20px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.retry-btn:hover {
  background-color: rgba(255, 107, 107, 0.1);
}

/* ======== LOGOUT BUTTON ======== */
.logout-container {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 150px;
  background-color: #3f403e;
  border-top: 2px solid white;
  z-index: 1001;
}

.logout-btn {
  width: 100%;
  padding: 12px 8px;
  background-color: #d32f2f;
  border: none;
  color: white;
  cursor: pointer;
  transition: background-color 0.3s;
  text-align: left;
  font-size: 16px;
}

.logout-btn:hover {
  background-color: #b71c1c;
}

.logout-btn .menu-item {
  color: white;
  font-weight: bold;
}

/* ======== HAMBURGER BUTTON ======== */
.hamburger {
  display: none;
  position: fixed;
  top: 10px;
  left: 10px;
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  z-index: 1100;
  cursor: pointer;
  padding: 0;
}

.hamburger span {
  display: block;
  position: absolute;
  height: 2px;
  width: 100%;
  background-color: black;
  left: 0;
  transition: 0.3s;
  border-radius: 1px;
}

.hamburger span.open {
  background-color: white;
}

.hamburger span:nth-child(1) {
  top: 4px;
}

.hamburger span:nth-child(2) {
  top: 11px;
}

.hamburger span:nth-child(3) {
  top: 18px;
}

.hamburger span.open:nth-child(1) {
  transform: rotate(45deg);
  top: 11px;
}

.hamburger span.open:nth-child(2) {
  opacity: 0;
}

.hamburger span.open:nth-child(3) {
  transform: rotate(-45deg);
  top: 11px;
}

/* ======== MOBILE OVERRIDE ======== */
@media (max-width: 567px) {
  .hamburger {
    display: block;
  }

  .aside-menu {
    width: 100vw;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    padding-top: 60px;
    padding-bottom: 80px;
    left: 0;
  }

  .aside-menu.menu-open {
    transform: translateX(0);
  }

  .aside-menu a {
    padding: 20px;
    font-size: 22px;
    border-left: none;
  }

  .menu-item {
    margin-left: 20px;
    font-size: 18px;
  }

  .menu-loading,
  .menu-error {
    padding: 20px;
  }

  .logout-container {
    width: 100vw;
  }

  .logout-btn {
    padding: 20px 8px;
    font-size: 20px;
  }
}

/* ======== DESKTOP ======== */
@media (min-width: 768px) {
  .aside-menu {
    width: 150px;
    left: 0;
    transform: none;
    padding-top: 0;
  }

  .aside-menu a {
    padding: 8px;
    font-size: 18px;
    border-left: none;
  }

  .menu-item {
    margin-left: 20px;
    font-size: 14px;
  }

  .hamburger {
    display: none;
  }

  .menu-loading,
  .menu-error {
    padding: 8px;
  }
}
</style>
